/* eslint-disable no-undef */
import { createAction } from "@reduxjs/toolkit";
import axios from "axios";
import { AUTHENTICATE, LOGIN } from "features/auth/reducer/AuthActions";
import { connectorInit } from "features/integrations/reducer/ConnectorAction";
import { getOrganizationSettings, getSubscriptionDetails } from "features/settings/reducer/SettingsActions";
import { getAllAuthorizedUsers, getAllRegisteredUsers, getAllRoles, getSettingUser, upsertSettingUser } from "features/settings/reducer/UserActions";
import { updateAllStoresStates } from "shared/hooks/useNetworkStatus";
import { configHeaderAxios } from "shared/Routes/route-validation";
import { publicRoutes } from "shared/Routes/routes";
import { brandValueInit, cxGoalsInit, getAllCxMetrics, getAllInsightTypes, getAllStakeholderRoles, getAllTeams, getNotifs, groupInit, iconsInit, libraryInit, measureInit, priorityInit, statusInit, tagInit, touchPointCategoriesInit, touchPointInit } from "./index";
import { base } from "./store";
//reducers
export const customMiddleWare = (store) => (next) => async (action) => {
	try {
		if (action.type === LOGIN || action.type === AUTHENTICATE) {
			return next(action);
		}
		//TODO Refactoring token should be anywhere and not only at the end
		const urlParams = new URLSearchParams(window.location.search);
		const pdfToken = urlParams.get("token");
		if (action?.error?.response?.status === 401 && pdfToken === null) {

			if (action?.error?.response?.data === "Refused") {
				const apiCall = action?.error?.request?.responseURL?.split("/api/")[1]?.split("/")[0];

				store.dispatch({ type: `NO_ACCESS_${apiCall}` });
			} else {
				store.dispatch({ type: "LOGOUT" });
				process.env.NODE_ENV === "development" && console.log("LOG OUT From Middleware");
			}
		}

		return next(action);
	} catch (e) {
		console.error(e);
	}
};

export const cancelableRequestsMiddleware = (store) => (next) => (action) => {
	if (action.request && action.request.method === "GET") {
		const requestType = action.type;

		// Use a static variable to store the cancellation tokens
		if (!cancelableRequestsMiddleware.cancellations) {
			cancelableRequestsMiddleware.cancellations = {};
		}

		const previousCancelTokenSource = cancelableRequestsMiddleware.cancellations[requestType];
		if (previousCancelTokenSource) {
			// console.log(`Cancelling previous request of type ${requestType}`);
			previousCancelTokenSource.cancel(`Cancelled previous request of type ${requestType}`);
		}

		// Create a new cancel token source for the new request
		const cancelTokenSource = axios.CancelToken.source();
		action.request.cancelToken = cancelTokenSource.token; // Attach the cancel token to the request
		cancelableRequestsMiddleware.cancellations[requestType] = cancelTokenSource;

		// Clean up after the request is done
		const cleanup = () => {
			delete cancelableRequestsMiddleware.cancellations[requestType];
		};

		if (action.promise && typeof action.promise.then === "function") {
			action.promise.then(cleanup).catch(cleanup);
		}
	}
	return next(action);
};

function isObjectEmpty(obj, workspaceId) {
	if (!obj || Object.keys(obj).length === 0) {
		return true
	}

	if (workspaceId) {
		const items = Object.values(obj).flat();
		if (items[0]?.workspaceId) {
			return items[0]?.workspaceId !== workspaceId
		}
	}
	return false
}

export const checkEmptyListMiddleware = (store) => (next) => (action) => {
	// Proceed with the current action
	const result = next(action);

	// Check for the PERSIST/REHYDRATE action from redux-persist
	const urlParams = new URLSearchParams(window.location.search);
	const pdfToken = urlParams.get("token");
	if (action.type === "persist/REHYDRATE") {
		const state = store.getState();

		const currentToken = pdfToken || state.auth?.token

		if (currentToken) {
			configHeaderAxios(currentToken);
		}
		if (currentToken && (typeof currentToken === 'string') && currentToken !== state.auth?.token) {
			const setAuth = createAction("SET_AUTHENTICATED");
			store.dispatch(setAuth(currentToken));
		}
		const paths = window.location.pathname.split("/");

		if (currentToken && !publicRoutes.some((path) => paths.includes(path))) {
			/// **** CHECK IF SWITCH WORkSPACE **** ///
			const workspaceId = base.split("/")[2];
			if (state.settings?.userSetting?.workspaceId !== workspaceId) {
				console.log("resetAllStore")
				const resetNotif = createAction("RESET_NOTIFICATIONS");
				store.dispatch(resetNotif());
				upsertSettingUser({ userSetting: { id: state.settings?.userSetting?.id, workspaceId: workspaceId }, modifiedAttribNames: ["workspaceId"] });
				updateAllStoresStates(store.dispatch);
				return result;
			}

			/// **** SETTINGS **** ///

			// Check if `subscriptionDetails` in `settings` is empty
			if (isObjectEmpty(state.settings?.subscriptionDetails)) {
				// Dispatch an action to fetch subscription details
				store.dispatch(getSubscriptionDetails());
			}
			if (isObjectEmpty(state.settings?.userSetting)) {
				store.dispatch(getSettingUser());
			}
			if (isObjectEmpty(state.settings?.organization)) {
				store.dispatch(getOrganizationSettings());
			}
			if (isObjectEmpty(state.settings?.authorizedUsers)) {
				store.dispatch(getAllAuthorizedUsers());
			}
			if (isObjectEmpty(state.settings?.users)) {
				store.dispatch(getAllRegisteredUsers())
			}
			
			if (state.settings?.connector?.length === 0) {
				store.dispatch(connectorInit());
			}
			if (isObjectEmpty(state.settings?.teams)) {
				store.dispatch(getAllTeams());
			}
			if (!state.settings?.roles || state.settings?.roles?.length === 0) {
				store.dispatch(getAllRoles());
			}
			/// **** LIBRARIES **** ///
			const { group, tags, icons, customLibraries, touchPoint, brandValues, notifications,
				measures, insightTypes, stakeholderRoles, cxMetrics, cxGoals, touchPointCategories } = state.libraries || {}
			if (checkArrayAndWorkspace(group, workspaceId)) {
				store.dispatch(groupInit());
			}
			if (checkArrayAndWorkspace(tags, workspaceId)) {
				store.dispatch(tagInit());
			}
			if (checkArrayAndWorkspace(icons, workspaceId)) {
				store.dispatch(iconsInit());
			}
			if (checkArrayAndWorkspace(customLibraries, workspaceId)) {
				store.dispatch(libraryInit());
			}
			if (checkArrayAndWorkspace(touchPoint, workspaceId)) {
				store.dispatch(touchPointInit());
			}
			if (checkArrayAndWorkspace(notifications, workspaceId)) {
				store.dispatch(getNotifs());
			}
			if (checkArrayAndWorkspace(brandValues, workspaceId)) {
				store.dispatch(brandValueInit());
			}
			if (isObjectEmpty(state.libraries?.priority, workspaceId)) {
				store.dispatch(priorityInit());
			}
			if (isObjectEmpty(state.libraries?.status, workspaceId)) {
				store.dispatch(statusInit());
			}
			if (checkArrayAndWorkspace(measures, workspaceId)) {
				store.dispatch(measureInit());
			}
			if (checkArrayAndWorkspace(insightTypes, workspaceId)) {
				store.dispatch(getAllInsightTypes());
			}
			if (checkArrayAndWorkspace(stakeholderRoles, workspaceId)) {
				store.dispatch(getAllStakeholderRoles());
			}
			if (checkArrayAndWorkspace(cxMetrics, workspaceId)) {
				store.dispatch(getAllCxMetrics());
			}
			if (checkArrayAndWorkspace(cxGoals, workspaceId)) {
				store.dispatch(cxGoalsInit());
			}
			if (checkArrayAndWorkspace(touchPointCategories, workspaceId)) {
				store.dispatch(touchPointCategoriesInit());
			}

		}
	}

	return result;
};

function checkArrayAndWorkspace(array, workspaceId) {
	if (!array && array?.length === 0) {
		return true;
	}
	if (array?.[0]?.workspaceId) {
		return workspaceId !== array[0].workspaceId
	}
	return false
}